import { CookieConsent } from '@/context/CookieConsentStore'
import { logger } from '@/utils/logger'
import { captureInternalTrafficFromLocation, tagGoogleAnalyticsInternalTraffic } from './internalTraffic'

declare global {
    interface Window {
        dataLayer?: any[]
        clarity?: (...args: any[]) => void
    }
}

export const initializeAnalytics = (consent: CookieConsent) => {
    // Microsoft Clarity
    if (consent.analytics && !window.clarity) {
        const CLARITY_PROJECT_ID = process.env.REACT_APP_CLARITY_PROJECT_ID
        logger.analytics.init('Microsoft Clarity', CLARITY_PROJECT_ID)
        
        // SECURITY NOTE: This script injection is safe because:
        // 1. CLARITY_PROJECT_ID comes from environment variables (not user input)
        // 2. The script content is static and trusted (Microsoft Clarity's official snippet)
        // 3. The script is only executed with explicit user consent
        const script = document.createElement('script')
        script.id = 'clarity-script'
        script.innerHTML = `
            (function(c,l,a,r,i,t,y){
                c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
                y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
            })(window, document, "clarity", "script", "${CLARITY_PROJECT_ID}");
        `
        document.head.appendChild(script)
        logger.analytics.initialized('Microsoft Clarity')
    } else if (consent.analytics) {
        logger.analytics.already('Microsoft Clarity')
    } else {
        logger.analytics.denied('Microsoft Clarity')
    }

    // Google Analytics
    if (consent.analytics && !window.gtag) {
        const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID
        logger.analytics.init('Google Analytics GA4', GA_MEASUREMENT_ID)

        // Initialize Google Analytics (GA4)
        const script = document.createElement('script')
        script.id = 'ga4-script'
        script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`
        script.async = true
        document.head.appendChild(script)

        window.gtag = function () {
            // @ts-ignore
            window.dataLayer = window.dataLayer || []
            // @ts-ignore
            window.dataLayer.push(arguments)
        }

        window.gtag('js', new Date())
        // Staff browsers are sent as internal traffic, which Google Analytics' internal traffic filter removes.
        captureInternalTrafficFromLocation()
        tagGoogleAnalyticsInternalTraffic()
        window.gtag('config', GA_MEASUREMENT_ID, {
            anonymize_ip: true,
            cookie_flags: 'SameSite=None;Secure'
        })
        logger.analytics.initialized('Google Analytics GA4')
    } else if (consent.analytics) {
        logger.analytics.already('Google Analytics')
    } else {
        logger.analytics.denied('Google Analytics')
    }

    // Meta Pixel
    if (consent.marketing && !window.fbq) {
        const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID
        logger.analytics.init('Meta Pixel', META_PIXEL_ID)
        
        // SECURITY NOTE: This script injection is safe because:
        // 1. META_PIXEL_ID comes from environment variables (not user input)
        // 2. The script content is static and trusted (Facebook's official snippet)
        // 3. The script is only executed with explicit user marketing consent
        const script = document.createElement('script')
        script.id = 'fb-pixel-init'
        script.innerHTML = `
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '${META_PIXEL_ID}');
            fbq('track', 'PageView');
            // Initialization completed - logged by analytics.init
        `
        document.head.appendChild(script)

        // Add Meta Pixel noscript element if it doesn't exist
        if (!document.querySelector('noscript img[src*="facebook.com/tr"]')) {
            const noscript = document.createElement('noscript')
            noscript.innerHTML = `
                <img height="1" width="1" style="display:none"
                src="https://www.facebook.com/tr?id=${META_PIXEL_ID}&ev=PageView&noscript=1"/>
            `
            document.body.appendChild(noscript)
            logger.debug('analytics', 'Meta Pixel noscript element added')
        }
    } else if (consent.marketing) {
        logger.analytics.already('Meta Pixel')
    } else {
        logger.analytics.denied('Meta Pixel')
    }
}

export const updateAnalyticsConsent = (consent: CookieConsent) => {
    // Update Microsoft Clarity consent
    if (window.clarity) {
        if (!consent.analytics) {
            logger.analytics.consent('Microsoft Clarity', 'revoke')
            removeMicrosoftClarity()
        }
    } else if (consent.analytics) {
        logger.analytics.consent('Microsoft Clarity', 'grant')
        initializeAnalytics(consent)
    }

    // Update Google Analytics consent
    if (window.gtag) {
        if (!consent.analytics) {
            logger.analytics.consent('Google Analytics', 'revoke')
            window.gtag('consent', 'update', {
                analytics_storage: 'denied',
                ad_storage: 'denied'
            })
            removeGoogleAnalytics()
        } else {
            logger.analytics.consent('Google Analytics', 'grant')
            window.gtag('consent', 'update', {
                analytics_storage: 'granted',
                ad_storage: consent.marketing ? 'granted' : 'denied'
            })
        }
    } else if (consent.analytics) {
        logger.analytics.consent('Google Analytics', 'grant')
        initializeAnalytics(consent)
    }

    // Update Meta Pixel consent
    if (window.fbq) {
        if (!consent.marketing) {
            logger.analytics.consent('Meta Pixel', 'revoke')
            window.fbq('consent', 'revoke')
            removeMetaPixel()
        } else {
            logger.analytics.consent('Meta Pixel', 'grant')
            window.fbq('consent', 'grant')
        }
    } else if (consent.marketing) {
        logger.analytics.consent('Meta Pixel', 'grant')
        initializeAnalytics(consent)
    }
}

// Function to clean up tracking scripts when consent is withdrawn
export const removeTrackingScripts = () => {
    removeGoogleAnalytics()
    removeMetaPixel()
    removeMicrosoftClarity()
}

// Helper function to remove Microsoft Clarity
const removeMicrosoftClarity = () => {
    logger.analytics.removed('Microsoft Clarity')

    // Remove Clarity script
    const clarityScript = document.querySelector('script[src*="clarity.ms/tag"]')
    if (clarityScript) {
        clarityScript.remove()
        logger.debug('analytics', 'Microsoft Clarity removed clarity.ms script')
    }

    // Remove initialization script
    const clarityInitScript = document.querySelector('script[id="clarity-script"]')
    if (clarityInitScript) {
        clarityInitScript.remove()
        logger.debug('analytics', 'Microsoft Clarity removed initialization script')
    }

    // Clear global variable
    delete window.clarity
    logger.debug('analytics', 'Microsoft Clarity cleared global variable')
}

// Helper function to remove Google Analytics
const removeGoogleAnalytics = () => {
    logger.analytics.removed('Google Analytics')

    // Remove GA4 script
    const gaScript = document.querySelector('script[src*="googletagmanager.com/gtag/js"]')
    if (gaScript) {
        gaScript.remove()
        logger.debug('analytics', 'Google Analytics removed gtag.js script')
    }

    // Remove initialization script
    const gaInitScript = document.querySelector('script[id="ga4-script"]')
    if (gaInitScript) {
        gaInitScript.remove()
        logger.debug('analytics', 'Google Analytics removed initialization script')
    }

    // Clear global variables
    delete window.gtag
    delete window.dataLayer
    logger.debug('analytics', 'Google Analytics cleared global variables')
}

// Helper function to remove Meta Pixel
const removeMetaPixel = () => {
    logger.analytics.removed('Meta Pixel')
    // Remove Meta Pixel scripts
    const fbScript = document.querySelector('script[src*="connect.facebook.net/en_US/fbevents.js"]')
    if (fbScript) {
        fbScript.remove()
        logger.debug('analytics', 'Meta Pixel removed fbevents.js script')
    }

    // Remove initialization script
    const fbInitScript = document.querySelector('script[id="fb-pixel-init"]')
    if (fbInitScript) {
        fbInitScript.remove()
        logger.debug('analytics', 'Meta Pixel removed initialization script')
    }

    // Remove Meta Pixel noscript
    const fbNoscript = document.querySelector('noscript img[src*="facebook.com/tr"]')
    if (fbNoscript) {
        fbNoscript.parentElement?.remove()
        logger.debug('analytics', 'Meta Pixel removed noscript element')
    }

    // Clear global variables
    delete window.fbq
    logger.debug('analytics', 'Meta Pixel cleared global variables')
} 